/**
 * fireberryAccountToNoteRecord — the missing hop for Gal's real instance engine
 *
 * The universal 8-view engine (src/app/dashboard/entities/[type]/page.tsx —
 * Table/Board/List/Calendar/Gantt/Timeline/Gallery/Stream) doesn't render
 * anything view-specific from the source table. It only knows one shape:
 * `NoteRecord` (src/lib/entities/types.ts). Every view is already agnostic
 * to where a row came from — Rendering Separation (עיקרון 5) holds here by
 * construction. The reason `fireberry_account` returned 404/empty despite
 * being `status: "ready"` in entitySourceResolver.ts is that nothing ever
 * produced a NoteRecord for it — fetchNotes()/the [id] route only knew
 * `vb_records`.
 *
 * This is that missing mapper. It reuses fbAccountToCompany (the canonical,
 * already-audited mirror read) instead of re-parsing fb_records.data itself.
 *
 * Read-only. fireberry-mirror never accepts writes — see fbAccountToCompany's
 * own header and data-contract.ts ("שלב 1 = הכל read-only").
 */

import type { NoteRecord } from "@/lib/entities/types";

import { fbAccountToCompany, type FbAccountRow } from "./fbAccountToCompany";

export function fireberryAccountToNoteRecord(row: FbAccountRow): NoteRecord {
  const account = fbAccountToCompany(row);

  return {
    id: account.id,
    title: account.name || "(ללא שם)",
    // No TipTap body exists for a mirror row (Sprint 1: "אין גוף בשלב הזה").
    content: null,
    record_type: "fireberry_account",
    entity_type: "fireberry_account",
    // Flat fields views read via meta.<key> — same convention as vb_records'
    // lens-on-record pattern, just sourced from the mirror instead.
    meta: {
      phone: account.phone,
      email: account.email,
      city: account.city,
      address: account.address,
      id_number: account.id_number,
      account_number: account.account_number,
      industry: account.industry,
      status: account.status,
      primary_contact: account.primary_contact,
      fireberry_id: account.fireberry_id,
    },
    status: account.status ?? "active",
    source: "fireberry-mirror",
    is_deleted: false,
    created_by: null,
    created_at: account.created_at ?? new Date(0).toISOString(),
    last_edited_at: account.updated_at ?? account.created_at ?? new Date(0).toISOString(),
  };
}
